import { useCallback, useRef, useState } from 'react';
import { classifyRequestError, type RequestFailure } from '../display';

/**
 * Fila de requisições com concorrência limitada e **estado visível**. Genérica
 * de propósito: a importação de prova enfileira uma questão por linha do CSV,
 * mas qualquer ação em massa (excluir/transferir várias questões) usa a mesma
 * fila e o mesmo painel.
 *
 * Pool de N workers em cima de uma lista em `ref` (não em estado): o worker
 * precisa ler o próximo item pendente no instante em que termina o anterior, e
 * estado do React é assíncrono — dois workers leriam a mesma lista velha e
 * pegariam o mesmo item. O estado guarda só o que a UI renderiza.
 *
 * **Uma tentativa por item, e o reenvio é o botão.** A fila já teve backoff
 * automático (4 tentativas: 1 imediata + 3 reenvios com espera crescente) para
 * erro transitório. Saiu: quando o servidor falha de verdade — foi um 500 de
 * política de upload que motivou isto —, as 3 repetições só transformam uma
 * falha em quatro, atrasam o fim do lote e escondem, atrás de um countdown, a
 * pergunta que importa ("isto vai voltar sozinho ou não?"). Item que falha vira
 * pendência na hora, com o erro do servidor à vista; quem decide reenviar é
 * quem está olhando, e para isso existe um clique.
 *
 * Uma falha não trava a fila: o item sai de pendente e os outros seguem.
 */

export type QueueItemStatus = 'queued' | 'processing' | 'done' | 'failed';

export interface QueueItem {
  id: string;
  /** O que aparece na UI (ex.: "Questão 7"). */
  label: string;
  status: QueueItemStatus;
  failure?: RequestFailure;
  /**
   * Quantas vezes este item já rodou (1 = a primeira). Só cresce por clique no
   * reenvio — é o que diferencia "falhou" de "falhou de novo" na tela, sem o
   * qual clicar no botão e receber o mesmo card parece não ter feito nada.
   */
  attempt: number;
}

export interface QueueJob {
  id: string;
  label: string;
  run: () => Promise<unknown>;
}

export interface QueueCounts {
  queued: number;
  processing: number;
  done: number;
  failed: number;
}

export function useRequestQueue(concurrency: number) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const jobsRef = useRef(new Map<string, () => Promise<unknown>>());
  const pendingRef = useRef<string[]>([]);
  const attemptsRef = useRef(new Map<string, number>());
  const workersRef = useRef(0);

  const patch = useCallback((id: string, next: Partial<QueueItem>) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...next } : item)),
    );
  }, []);

  const spawnWorkers = useCallback(() => {
    while (workersRef.current < concurrency && pendingRef.current.length > 0) {
      workersRef.current += 1;
      void (async () => {
        try {
          for (;;) {
            const id = pendingRef.current.shift();
            if (id === undefined) break;
            const run = jobsRef.current.get(id);
            if (!run) continue;

            const attempt = (attemptsRef.current.get(id) ?? 0) + 1;
            attemptsRef.current.set(id, attempt);
            patch(id, { status: 'processing', attempt, failure: undefined });

            try {
              await run();
              patch(id, { status: 'done', failure: undefined });
            } catch (err) {
              patch(id, {
                status: 'failed',
                failure: classifyRequestError(err, 'Falha na requisição'),
              });
            }
          }
        } finally {
          workersRef.current -= 1;
        }
      })();
    }
  }, [concurrency, patch]);

  const enqueue = useCallback(
    (jobs: QueueJob[]) => {
      for (const job of jobs) {
        jobsRef.current.set(job.id, job.run);
        attemptsRef.current.set(job.id, 0);
      }
      pendingRef.current.push(...jobs.map((job) => job.id));
      setItems((prev) => [
        ...prev,
        ...jobs.map((job) => ({
          id: job.id,
          label: job.label,
          status: 'queued' as const,
          attempt: 0,
        })),
      ]);
      spawnWorkers();
    },
    [spawnWorkers],
  );

  /**
   * Reenfileira o item agora. É o **único** caminho de reenvio que existe — o
   * `run` do job lê os dados de refs, então já sai com o payload corrigido se o
   * dono da fila tiver consertado a causa no meio tempo.
   */
  const retry = useCallback(
    (id: string) => {
      if (!jobsRef.current.has(id)) return;
      patch(id, { status: 'queued', failure: undefined });
      pendingRef.current.push(id);
      spawnWorkers();
    },
    [patch, spawnWorkers],
  );

  /** Zera a fila. Workers em voo terminam o item atual e morrem sem efeito. */
  const reset = useCallback(() => {
    jobsRef.current = new Map();
    attemptsRef.current = new Map();
    pendingRef.current = [];
    setItems([]);
  }, []);

  const counts: QueueCounts = {
    queued: items.filter((item) => item.status === 'queued').length,
    processing: items.filter((item) => item.status === 'processing').length,
    done: items.filter((item) => item.status === 'done').length,
    failed: items.filter((item) => item.status === 'failed').length,
  };

  return {
    items,
    counts,
    isRunning: counts.queued + counts.processing > 0,
    enqueue,
    retry,
    reset,
  };
}
